import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PageHeader from '../../../components/common/PageHeader';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString('fr-FR') : '—');
const fmtCurrency = (value) => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
}).format(Number(value || 0));

function MetricCard({ label, value, tone = 'default' }) {
  const tones = {
    default: { bg: '#f8fafc', border: '#e2e8f0', color: '#0f172a' },
    success: { bg: '#ecfdf3', border: '#6ce9a6', color: '#027a48' },
    warning: { bg: '#fff7ed', border: '#fed7aa', color: '#c2410c' },
    error: { bg: '#fef2f2', border: '#fecaca', color: '#b91c1c' },
  };

  const palette = tones[tone] || tones.default;

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1.5,
        py: 1.25,
        minWidth: 150,
        borderRadius: 3,
        backgroundColor: palette.bg,
        borderColor: palette.border,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="h6" sx={{ color: palette.color, fontWeight: 800 }}>{value}</Typography>
    </Paper>
  );
}

export default function FacturesPage() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [quickFilter, setQuickFilter] = useState('all');

  const loadInvoices = async () => {
    setIsLoading(true);
    setError('');

    try {
      const { data } = await api.get('/api/factures', {
        params: {
          limit: 300,
          sortBy: 'dueDate',
          sortOrder: 'asc',
        },
      });

      setInvoices(data?.data || []);
      setSummary(data?.summary || null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur lors du chargement des factures.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setError('');
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post('/api/import/invoices', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setImportResult(data);
      await loadInvoices();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur lors de l import des factures.');
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  const filteredInvoices = useMemo(() => {
    if (quickFilter === 'paid') {
      return invoices.filter((invoice) => invoice.paid);
    }
    if (quickFilter === 'unpaid') {
      return invoices.filter((invoice) => !invoice.paid);
    }
    if (quickFilter === 'overdue') {
      return invoices.filter((invoice) => invoice?.computed?.status === 'en_retard');
    }
    return invoices;
  }, [invoices, quickFilter]);

  const columns = useMemo(() => [
    {
      accessorKey: 'invoiceNumber',
      header: 'Piece',
      size: 140,
      Cell: ({ row }) => (
        <Stack spacing={0.35}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {row.original.invoiceNumber}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fmtDate(row.original.invoiceDate)}
          </Typography>
        </Stack>
      ),
    },
    {
      accessorKey: 'clientName',
      header: 'Client',
      size: 240,
    },
    {
      accessorKey: 'dueDate',
      header: 'Echeance',
      size: 130,
      Cell: ({ row }) => fmtDate(row.original.dueDate),
    },
    {
      accessorKey: 'paymentType',
      header: 'Mode de reglement',
      size: 170,
      Cell: ({ row }) => row.original.paymentType || '—',
    },
    {
      accessorKey: 'amount',
      header: 'Montant',
      size: 130,
      Cell: ({ row }) => fmtCurrency(row.original.amount),
    },
    {
      accessorKey: 'paid',
      header: 'Reglee',
      size: 140,
      Cell: ({ row }) => (
        row.original.paid ? (
          <Chip
            size="small"
            color="success"
            icon={<CheckCircleIcon />}
            label={`Oui • ${fmtDate(row.original.paidAt)}`}
          />
        ) : (
          <Chip
            size="small"
            color="warning"
            icon={<WarningAmberIcon />}
            label="Non"
          />
        )
      ),
    },
    {
      accessorKey: 'computed.status',
      header: 'Statut',
      size: 120,
      Cell: ({ row }) => {
        const status = row.original?.computed?.status;
        const config = {
          reglee: { label: 'Reglee', color: 'success' },
          en_retard: { label: 'En retard', color: 'error' },
          echeance_aujourdhui: { label: 'Aujourd hui', color: 'warning' },
          a_venir: { label: 'A venir', color: 'default' },
          a_traiter: { label: 'A traiter', color: 'default' },
        }[status] || { label: 'A traiter', color: 'default' };

        return <Chip size="small" color={config.color} label={config.label} />;
      },
    },
  ], []);

  const table = useMaterialReactTable({
    localization: MRT_Localization_FR,
    enableGlobalFilter: true,
    enableColumnFilters: true,

    columns,
    data: filteredInvoices,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,

      density: 'compact',
      sorting: [{ id: 'dueDate', desc: false }],
    },
    renderTopToolbarCustomActions: () => (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ p: 1, width: '100%' }}
      >
        <Button
          component="label"
          variant="contained"
          startIcon={<UploadFileIcon />}
          disabled={isImporting}
        >
          Importer les factures
          <input hidden type="file" accept=".csv,.xls,.xlsx" onChange={handleImport} />
        </Button>

        <TextField
          select
          size="small"
          label="Filtre rapide"
          value={quickFilter}
          onChange={(event) => setQuickFilter(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">Toutes</MenuItem>
          <MenuItem value="unpaid">Non reglees</MenuItem>
          <MenuItem value="paid">Reglees</MenuItem>
          <MenuItem value="overdue">En retard</MenuItem>
        </TextField>

        <Button
          variant="text"
          startIcon={<RefreshIcon />}
          onClick={loadInvoices}
          disabled={isLoading}
        >
          Rafraichir
        </Button>
      </Stack>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        background: 'linear-gradient(180deg, #f7fafc 0%, #eef6f7 100%)',
        minHeight: '100%',
      }}
    >
      <Stack spacing={2.5}>
        <PageHeader section="Administratif" title="Factures clients" />

        {isLoading || isImporting ? <LinearProgress /> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {importResult ? (
          <Alert severity="success">
            Import termine: {importResult.totals?.created || 0} creees, {importResult.totals?.updated || 0} mises a jour,
            {` ${importResult.totals?.errors || 0} erreur(s).`}
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 4 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
            <MetricCard label="Factures actives" value={summary?.totalActive || 0} />
            <MetricCard label="Reglees" value={summary?.paidCount || 0} tone="success" />
            <MetricCard label="Non reglees" value={summary?.unpaidCount || 0} tone="warning" />
            <MetricCard label="En retard" value={summary?.overdueCount || 0} tone="error" />
            <MetricCard label="Montant en retard" value={fmtCurrency(summary?.overdueAmount || 0)} tone="error" />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <MaterialReactTable table={table} />
        </Paper>
      </Stack>
    </Box>
  );
}
